import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Building2, Plus, Pencil, Trash2, Users, TrendingUp, AlertTriangle,
  X, DollarSign, FileText, Shield, ChevronDown, ChevronUp,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const CURRENCIES = ['GBP', 'USD', 'EUR', 'PKR', 'INR', 'AUD', 'CAD', 'JPY', 'CHF'];
const RISK_COLORS = { High: '#ef4444', Medium: '#f59e0b', Low: '#22c55e', Unknown: '#94a3b8' };

function fmt(v) {
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}m`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return v?.toFixed(0) || '0';
}

function OrgAnalytics({ orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/organizations/${orgId}/analytics`);
        setData(res.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    })();
  }, [orgId]);

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;
  if (!data) return null;

  const riskPie = Object.entries(data.risk_distribution).map(([k, v]) => ({ name: k, value: v, fill: RISK_COLORS[k] || RISK_COLORS.Unknown }));
  const cur = data.organization?.default_currency || 'GBP';
  const sym = { GBP: '£', USD: '$', EUR: '€', INR: '₹', PKR: 'Rs' }[cur] || cur + ' ';

  return (
    <div className="space-y-4 mt-4" data-testid="org-analytics">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Total Spend</p>
            <p className="text-xl font-bold font-mono">{sym}{fmt(data.total_spend)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> Suppliers</p>
            <p className="text-xl font-bold font-mono">{data.supplier_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Transactions</p>
            <p className="text-xl font-bold font-mono">{data.transaction_count.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> High Risk</p>
            <p className="text-xl font-bold font-mono text-red-500">{data.high_risk_suppliers}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Spend Trend */}
        {data.spend_trend.length > 0 && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-xs text-muted-foreground">Spend Trend</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={data.spend_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={m => m.slice(5)} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={v => fmt(v)} width={40} />
                  <Tooltip formatter={v => [`${sym}${v.toLocaleString()}`, 'Spend']} labelFormatter={l => `Month: ${l}`} />
                  <Line type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Risk Distribution */}
        {riskPie.length > 0 && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-xs text-muted-foreground">Risk Distribution</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3 flex items-center justify-center">
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie data={riskPie} cx="50%" cy="50%" innerRadius={25} outerRadius={45} dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {riskPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Top Categories */}
        {data.top_categories.length > 0 && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-xs text-muted-foreground">Top Categories</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={data.top_categories} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => fmt(v)} />
                  <YAxis type="category" dataKey="category" width={80} tick={{ fontSize: 8 }} />
                  <Tooltip formatter={v => [`${sym}${v.toLocaleString()}`, 'Spend']} />
                  <Bar dataKey="spend" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [form, setForm] = useState({ name: '', industry: '', contact_email: '', default_currency: 'GBP', subscribed_features: [], ai_recommendations_scope: 'all', ai_insights_scope: 'all' });
  const [saving, setSaving] = useState(false);
  const [subOverview, setSubOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('orgs');

  const PREMIUM_FEATURES = [
    // ── Procurement Analytics
    { key: 'p2p_analytics', label: 'P2P Analytics', group: 'Procurement Analytics' },
    { key: 'comparative_analysis', label: 'Comparative Analysis', group: 'Procurement Analytics' },
    { key: 'benchmark', label: 'Industry Benchmark', group: 'Procurement Analytics' },
    { key: 'forecasting', label: 'Spend Forecasting', group: 'Procurement Analytics' },
    { key: 'currency_analytics', label: 'Currency Analytics', group: 'Procurement Analytics' },
    { key: 'invoice_aging', label: 'Invoice Aging Analysis', group: 'Procurement Analytics' },
    // ── Supplier & Category Management
    { key: 'supplier_management', label: 'Supplier Management', group: 'Supplier & Category Management' },
    { key: 'category_management', label: 'Category Management', group: 'Supplier & Category Management' },
    // ── Operational Analytics
    { key: 'invoice_dedup', label: 'Invoice Deduplication', group: 'Operational Analytics' },
    { key: 'on_contract_maverick', label: 'On-Contract & Maverick Spend', group: 'Operational Analytics' },
    { key: 'governance', label: 'Governance Dashboard', group: 'Operational Analytics' },
    // ── Data Quality & Integration
    { key: 'data_quality', label: 'Data Quality', group: 'Data Quality & Integration' },
    { key: 'connectors', label: 'Data Connectors', group: 'Data Quality & Integration' },
    { key: 'ml_insights', label: 'ML Insights (Auto-Classification & Anomalies)', group: 'Data Quality & Integration' },
    // ── AI & Reporting
    { key: 'ai_recommendations', label: 'AI Recommendations & Insights', group: 'AI & Reporting' },
    { key: 'action_plan', label: 'AI Action Plans', group: 'AI & Reporting' },
    { key: 'savings_report', label: 'Savings Potential Report', group: 'AI & Reporting' },
    { key: 'executive_digest', label: 'Executive Monday Digest', group: 'AI & Reporting' },
    { key: 'signal_intelligence', label: 'Signal Intelligence', group: 'AI & Reporting' },
  ];

  useEffect(() => { fetchOrgs(); fetchSubOverview(); }, []);

  // Handle deep links from Platform Admin: ?new=1 opens the create dialog,
  // ?edit=<id> opens the edit dialog once orgs load, ?tab=subs selects the subs tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      openCreate();
    }
    if (params.get('tab') === 'subs') {
      setActiveTab('subs');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId && orgs.length > 0 && !showForm) {
      const found = orgs.find((o) => o.id === editId);
      if (found) openEdit(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs]);

  const fetchSubOverview = async () => {
    try {
      const res = await api.get('/admin/subscriptions/overview');
      setSubOverview(res.data);
    } catch {}
  };

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/organizations');
      setOrgs(res.data);
    } catch { toast.error('Failed to load organizations'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditOrg(null);
    setForm({ name: '', industry: '', contact_email: '', default_currency: 'GBP', subscribed_features: [], ai_recommendations_scope: 'all', ai_insights_scope: 'all', org_type: 'independent', parent_organization_id: null });
    setShowForm(true);
  };

  const openEdit = (o) => {
    setEditOrg(o);
    setForm({
      name: o.name,
      industry: o.industry || '',
      contact_email: o.contact_email || '',
      default_currency: o.default_currency || 'GBP',
      subscribed_features: o.subscribed_features || [],
      ai_recommendations_scope: o.ai_recommendations_scope || 'all',
      ai_insights_scope: o.ai_insights_scope || 'all',
      ai_daily_cap_usd_override: o.ai_daily_cap_usd_override || '',
      org_type: o.org_type || 'independent',
      parent_organization_id: o.parent_organization_id || null,
    });
    setShowForm(true);
  };

  // Which features are eligible for the org being edited?
  // If a parent is selected, only features present on the parent can be enabled.
  const parentOrg = form.parent_organization_id ? orgs.find(x => x.id === form.parent_organization_id) : null;
  const eligibleFeatures = parentOrg ? new Set(parentOrg.subscribed_features || []) : null;
  const isFeatureEligible = (key) => !eligibleFeatures || eligibleFeatures.has(key);

  const toggleFeature = (key) => {
    if (!isFeatureEligible(key)) {
      toast.error(`Parent organization does not include this feature. Enable it on the holding company first.`);
      return;
    }
    setForm(p => ({
      ...p,
      subscribed_features: p.subscribed_features.includes(key)
        ? p.subscribed_features.filter(f => f !== key)
        : [...p.subscribed_features, key]
    }));
  };

  // If org_type changes to independent/holding, clear parent. If changes to subsidiary/branch, ensure parent required.
  const onOrgTypeChange = (t) => {
    setForm(p => ({
      ...p,
      org_type: t,
      parent_organization_id: (t === 'independent' || t === 'holding') ? null : p.parent_organization_id,
    }));
  };

  // When parent changes, prune features that aren't in the new parent's bundle
  const onParentChange = (parentId) => {
    const newParent = parentId ? orgs.find(x => x.id === parentId) : null;
    const allowed = newParent ? new Set(newParent.subscribed_features || []) : null;
    setForm(p => ({
      ...p,
      parent_organization_id: parentId || null,
      subscribed_features: allowed ? p.subscribed_features.filter(f => allowed.has(f)) : p.subscribed_features,
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Organization name is required');
    if ((form.org_type === 'subsidiary' || form.org_type === 'branch') && !form.parent_organization_id) {
      return toast.error(`A ${form.org_type} organization must have a parent (holding company).`);
    }
    const cap = form.ai_daily_cap_usd_override === '' || form.ai_daily_cap_usd_override == null ? 0 : Number(form.ai_daily_cap_usd_override);
    if (Number.isNaN(cap) || cap < 0 || cap > 10000) return toast.error('AI spend cap must be between 0 and 10,000 USD');
    const payload = { ...form, ai_daily_cap_usd_override: cap };
    setSaving(true);
    try {
      if (editOrg) {
        await api.put(`/admin/organizations/${editOrg.id}`, payload);
        toast.success('Organization updated');
      } else {
        await api.post('/admin/organizations', payload);
        toast.success('Organization created');
      }
      setShowForm(false);
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete organization "${o.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/organizations/${o.id}`);
      toast.success('Organization deleted');
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="organizations-page">
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="org-breadcrumb">
        <a href="/admin/platform" className="hover:text-foreground hover:underline">Platform Admin</a>
        <span>/</span>
        <span className="text-foreground">Organizations</span>
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground mt-1">{orgs.length} client organization(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('orgs')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeTab === 'orgs' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="tab-organizations"
            >
              Organizations
            </button>
            <button
              onClick={() => setActiveTab('subs')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${activeTab === 'subs' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="tab-subscriptions"
            >
              Subscriptions
            </button>
          </div>
          {activeTab === 'orgs' && (
            <Button size="sm" onClick={openCreate} data-testid="create-org-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Organization
            </Button>
          )}
        </div>
      </div>

      {/* Subscription Management Dashboard */}
      {activeTab === 'subs' && subOverview && (
        <div className="space-y-4" data-testid="subscription-dashboard">
          {/* Tier KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{subOverview.total_orgs}</p>
              <p className="text-xs text-muted-foreground">Total Orgs</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-slate-400"><CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{subOverview.tier_distribution?.basic || 0}</p>
              <p className="text-xs text-muted-foreground">Basic Tier</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{subOverview.tier_distribution?.standard || 0}</p>
              <p className="text-xs text-muted-foreground">Standard (1-4)</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-emerald-500"><CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{subOverview.tier_distribution?.premium || 0}</p>
              <p className="text-xs text-muted-foreground">Premium (5+)</p>
            </CardContent></Card>
          </div>

          {/* Feature Adoption */}
          <Card>
            <CardHeader className="py-3"><CardTitle className="text-sm">Feature Adoption</CardTitle><CardDescription className="text-xs">How many orgs subscribe to each premium feature</CardDescription></CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                {PREMIUM_FEATURES.map(f => {
                  const count = subOverview.feature_adoption?.[f.key] || 0;
                  const pct = subOverview.total_orgs ? Math.round((count / subOverview.total_orgs) * 100) : 0;
                  return (
                    <div key={f.key} className="flex items-center gap-3" data-testid={`adoption-${f.key}`}>
                      <span className="text-xs w-40 truncate">{f.label}</span>
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-16 text-right">{count} org{count !== 1 ? 's' : ''} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Per-Org Subscription Table */}
          <Card>
            <CardHeader className="py-3"><CardTitle className="text-sm">Organization Subscriptions</CardTitle></CardHeader>
            <CardContent className="pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Organization</th>
                      <th className="text-center py-2 px-2 font-medium">Users</th>
                      <th className="text-center py-2 px-2 font-medium">Features</th>
                      <th className="text-left py-2 px-2 font-medium">Subscribed Features</th>
                      <th className="text-center py-2 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(subOverview.organizations || []).map(org => (
                      <tr key={org.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2 font-medium">{org.name}</td>
                        <td className="py-2 px-2 text-center">{org.user_count}</td>
                        <td className="py-2 px-2 text-center">
                          <Badge variant={org.feature_count >= 5 ? 'default' : org.feature_count > 0 ? 'secondary' : 'outline'} className="text-xs">
                            {org.feature_count >= 5 ? 'Premium' : org.feature_count > 0 ? 'Standard' : 'Basic'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex flex-wrap gap-1">
                            {org.subscribed_features?.length > 0
                              ? org.subscribed_features.map(f => (
                                  <Badge key={f} variant="outline" className="text-[10px] px-1.5 py-0">{PREMIUM_FEATURES.find(pf => pf.key === f)?.label || f}</Badge>
                                ))
                              : <span className="text-muted-foreground">Basic only</span>}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setActiveTab('orgs'); const org_obj = orgs.find(o => o.id === org.id); if (org_obj) openEdit(org_obj); }} data-testid={`edit-sub-${org.id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Org List - only shown when activeTab === 'orgs' */}
      {activeTab === 'orgs' && (
        <>
      <div className="space-y-4">
        {orgs.map(o => (
          <Card key={o.id} className="overflow-hidden" data-testid="org-card">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{o.name}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                      {o.industry && <Badge variant="outline" className="text-xs">{o.industry}</Badge>}
                      {o.contact_email && <span>{o.contact_email}</span>}
                      <span className="font-mono">{o.default_currency || 'GBP'}</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {o.user_count || 0} users</span>
                      <span className="text-xs">{o.subscribed_features?.length || 0} premium features</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setExpandedOrg(expandedOrg === o.id ? null : o.id)} data-testid="expand-org-btn">
                    {expandedOrg === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    <span className="ml-1 text-xs">Analytics</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid="edit-org-btn">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(o)} data-testid="delete-org-btn">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </div>

              {expandedOrg === o.id && <OrgAnalytics orgId={o.id} />}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editOrg ? 'Edit Organization' : 'Create Organization'}</CardTitle>
              <CardDescription>{editOrg ? `Editing ${editOrg.name}` : 'Add a new client organization'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Organization Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Acme Corp" data-testid="org-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Organization Type</Label>
                  <Select value={form.org_type || 'independent'} onValueChange={onOrgTypeChange}>
                    <SelectTrigger data-testid="org-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="independent">Independent</SelectItem>
                      <SelectItem value="holding">Holding company</SelectItem>
                      <SelectItem value="subsidiary">Subsidiary</SelectItem>
                      <SelectItem value="branch">Branch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Parent (holding) organization</Label>
                  <Select
                    value={form.parent_organization_id || 'none'}
                    onValueChange={(v) => onParentChange(v === 'none' ? null : v)}
                    disabled={form.org_type === 'independent' || form.org_type === 'holding'}
                  >
                    <SelectTrigger data-testid="org-form-parent">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {orgs.filter(o => o.id !== editOrg?.id && !o.parent_organization_id).map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {parentOrg && (
                <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
                  This org inherits the subscription bundle of <span className="font-semibold">{parentOrg.name}</span>. You can enable any subset of its features below; features not on the parent are locked.
                </p>
              )}
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} placeholder="e.g. Manufacturing, Finance" data-testid="org-form-industry" />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="admin@company.com" data-testid="org-form-email" />
              </div>
              <div className="space-y-2">
                <Label>Default Currency</Label>
                <Select value={form.default_currency} onValueChange={v => setForm(p => ({ ...p, default_currency: v }))}>
                  <SelectTrigger data-testid="org-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>AI Recommendations Surface</Label>
                <p className="text-xs text-muted-foreground">Controls where AI recommendation panels appear for this tenant. Requires the "AI Recommendations & Insights" feature to be enabled below.</p>
                <div className="grid grid-cols-3 gap-2" data-testid="ai-recs-scope">
                  {[
                    { value: 'off', label: 'Off', hint: 'No recommendation panels anywhere' },
                    { value: 'dashboard', label: 'Dashboard only', hint: 'Recommendations only on the Dashboard' },
                    { value: 'all', label: 'All pages', hint: 'Recommendations on every analytics page' },
                  ].map(opt => {
                    const on = form.ai_recommendations_scope === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, ai_recommendations_scope: opt.value }))}
                        title={opt.hint}
                        className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
                          on
                            ? 'bg-primary/10 border-primary text-primary font-medium'
                            : 'border-border hover:bg-muted/50 text-muted-foreground'
                        }`}
                        data-testid={`ai-recs-scope-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label>AI Insights Surface</Label>
                <p className="text-xs text-muted-foreground">Controls where AI-generated insights are computed for this tenant. Independent of the recommendations surface above.</p>
                <div className="grid grid-cols-3 gap-2" data-testid="ai-insights-scope">
                  {[
                    { value: 'off', label: 'Off', hint: 'No AI insights anywhere' },
                    { value: 'dashboard', label: 'Dashboard only', hint: 'Insights only on the Dashboard' },
                    { value: 'all', label: 'All pages', hint: 'Insights on every analytics page' },
                  ].map(opt => {
                    const on = form.ai_insights_scope === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, ai_insights_scope: opt.value }))}
                        title={opt.hint}
                        className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
                          on
                            ? 'bg-primary/10 border-primary text-primary font-medium'
                            : 'border-border hover:bg-muted/50 text-muted-foreground'
                        }`}
                        data-testid={`ai-insights-scope-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Daily AI Spend Cap (USD)</Label>
                <p className="text-xs text-muted-foreground">
                  Leave blank for automatic: $50 + $5 per active user
                  {editOrg?.ai_daily_cap ? ` (currently $${editOrg.ai_daily_cap.cap_usd.toFixed(0)} — ${editOrg.ai_daily_cap.mode === 'override' ? 'override' : `${editOrg.ai_daily_cap.active_users} active users`})` : ''}.
                </p>
                <Input
                  type="number"
                  min="0"
                  max="10000"
                  value={form.ai_daily_cap_usd_override ?? ''}
                  onChange={e => setForm(p => ({ ...p, ai_daily_cap_usd_override: e.target.value }))}
                  placeholder="Automatic"
                  data-testid="org-form-ai-cap"
                />
              </div>
              <div className="space-y-2">
                <Label>Subscribed Features</Label>
                <p className="text-xs text-muted-foreground">Basic features (Dashboard, Spend Analysis, Distribution, Category Console, Commonality, Supplier Directory, Data Management, Admin) are always included.</p>
                <div className="space-y-3" data-testid="org-features-grid">
                  {Array.from(new Set(PREMIUM_FEATURES.map(f => f.group))).map(group => (
                    <div key={group} className="space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 pt-1">{group}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {PREMIUM_FEATURES.filter(f => f.group === group).map(f => {
                          const eligible = isFeatureEligible(f.key);
                          const on = form.subscribed_features.includes(f.key);
                          return (
                            <button
                              key={f.key}
                              type="button"
                              onClick={() => toggleFeature(f.key)}
                              disabled={!eligible}
                              title={!eligible ? `Not in ${parentOrg?.name}'s bundle` : ''}
                              className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
                                !eligible
                                  ? 'border-dashed border-border text-muted-foreground/60 cursor-not-allowed bg-muted/30'
                                  : on
                                    ? 'bg-primary/10 border-primary text-primary font-medium'
                                    : 'border-border hover:bg-muted/50 text-muted-foreground'
                              }`}
                              data-testid={`org-feature-${f.key}`}
                            >
                              {f.label}{!eligible ? ' 🔒' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="org-form-save">
                  {saving ? 'Saving...' : editOrg ? 'Update' : 'Create Organization'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      </>
      )}
    </div>
  );
}
